import React, { useEffect, useState } from 'react';
import { preferencesApi } from '../api/client';
import type { UserPreferences } from '../api/client';
import './Preferences.css';

const Preferences: React.FC = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<keyof UserPreferences>('dashboard');
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<Partial<UserPreferences>>({});

  const tabs: { key: keyof UserPreferences; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'privacy', label: 'Privacy', icon: '🔒' },
    { key: 'content', label: 'Content', icon: '📝' },
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'data', label: 'Data', icon: '💾' },
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await preferencesApi.getCurrentUserPreferences();
      setPreferences(response.data);
      setFormData(response.data);
      setHasChanges(false);
    } catch (err) {
      setError('Failed to load preferences');
      console.error('Preferences error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    
    try {
      const response = await preferencesApi.updateCurrentUserPreferences(formData);
      setPreferences(response.data);
      setFormData(response.data);
      setHasChanges(false);
      setError(null);
    } catch (err) {
      setError('Failed to update preferences');
      console.error('Update preferences error:', err);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all preferences to defaults?')) return;

    try {
      const response = await preferencesApi.getDefaultUserPreferences();
      const updated = await preferencesApi.updateCurrentUserPreferences(response.data);
      setPreferences(updated.data);
      setFormData(updated.data);
      setHasChanges(false);
      setError(null);
    } catch (err) {
      setError('Failed to reset preferences');
      console.error('Reset preferences error:', err);
    }
  };

  const handleDiscard = () => {
    setFormData(preferences || {});
    setHasChanges(false);
  };

  const handleSectionChange = (section: keyof UserPreferences, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const renderDashboardTab = () => {
    const section = formData.dashboard || {} as UserPreferences['dashboard'];
    return (
      <div className="preferences-section">
        <h3>📊 Dashboard Preferences</h3>
        <p className="section-description">Customize your SelfServe dashboard experience</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Layout & Appearance</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Default view when you sign in</label>
                <select
                  value={section.defaultView || 'overview'}
                  onChange={(e) => handleSectionChange('dashboard', 'defaultView', e.target.value)}
                  className="form-select"
                >
                  <option value="overview">Overview</option>
                  <option value="goals">Goals</option>
                  <option value="projects">Projects</option>
                  <option value="blogs">Blogs</option>
                </select>
              </div>
              <div className="preference-item">
                <label>Theme</label>
                <select
                  value={section.theme || 'light'}
                  onChange={(e) => handleSectionChange('dashboard', 'theme', e.target.value)}
                  className="form-select"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto (system)</option>
                </select>
              </div>
              <div className="preference-item">
                <label>Cards per row</label>
                <select
                  value={section.cardsPerRow || 3}
                  onChange={(e) => handleSectionChange('dashboard', 'cardsPerRow', parseInt(e.target.value))}
                  className="form-select"
                >
                  <option value={2}>2 cards</option>
                  <option value={3}>3 cards</option>
                  <option value={4}>4 cards</option>
                </select>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Display Options</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.compactMode || false}
                    onChange={(e) => handleSectionChange('dashboard', 'compactMode', e.target.checked)}
                  />
                  <span>Compact mode</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showWelcomeMessage ?? true}
                    onChange={(e) => handleSectionChange('dashboard', 'showWelcomeMessage', e.target.checked)}
                  />
                  <span>Show welcome message</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showQuickStats ?? true}
                    onChange={(e) => handleSectionChange('dashboard', 'showQuickStats', e.target.checked)}
                  />
                  <span>Show quick stats</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showRecentActivity ?? true}
                    onChange={(e) => handleSectionChange('dashboard', 'showRecentActivity', e.target.checked)}
                  />
                  <span>Show recent activity</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Auto-refresh</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.autoRefresh || false}
                    onChange={(e) => handleSectionChange('dashboard', 'autoRefresh', e.target.checked)}
                  />
                  <span>Auto-refresh dashboard</span>
                </label>
              </div>
              {section.autoRefresh && (
                <div className="preference-item">
                  <label>Refresh interval (seconds)</label>
                  <select
                    value={section.refreshInterval || 300}
                    onChange={(e) => handleSectionChange('dashboard', 'refreshInterval', parseInt(e.target.value))}
                    className="form-select"
                  >
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                    <option value={600}>10 minutes</option>
                    <option value={1800}>30 minutes</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNotificationsTab = () => {
    const section = formData.notifications || {} as UserPreferences['notifications'];
    return (
      <div className="preferences-section">
        <h3>🔔 Notification Preferences</h3>
        <p className="section-description">Control how you receive updates from SelfServe</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Email Notifications</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.email ?? true}
                    onChange={(e) => handleSectionChange('notifications', 'email', e.target.checked)}
                  />
                  <span>Email notifications</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.weeklyDigest ?? true}
                    onChange={(e) => handleSectionChange('notifications', 'weeklyDigest', e.target.checked)}
                  />
                  <span>Weekly digest</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Content Notifications</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.blogComments ?? true}
                    onChange={(e) => handleSectionChange('notifications', 'blogComments', e.target.checked)}
                  />
                  <span>Blog comments</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>System Notifications</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.systemUpdates ?? true}
                    onChange={(e) => handleSectionChange('notifications', 'systemUpdates', e.target.checked)}
                  />
                  <span>System updates</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPrivacyTab = () => {
    const section = formData.privacy || {} as UserPreferences['privacy'];
    return (
      <div className="preferences-section">
        <h3>🔒 Privacy Preferences</h3>
        <p className="section-description">Control who can see your SelfServe profile and content</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Profile Visibility</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Profile visibility</label>
                <select
                  value={section.profileVisibility || 'private'}
                  onChange={(e) => handleSectionChange('privacy', 'profileVisibility', e.target.value)}
                  className="form-select"
                >
                  <option value="public">Public (visible at /yourusername)</option>
                  <option value="private">Private (only you can see)</option>
                </select>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.allowPublicBlogAccess ?? true}
                    onChange={(e) => handleSectionChange('privacy', 'allowPublicBlogAccess', e.target.checked)}
                  />
                  <span>Allow public access to your blogs</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Profile Information</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showEmail || false}
                    onChange={(e) => handleSectionChange('privacy', 'showEmail', e.target.checked)}
                  />
                  <span>Show email address on profile</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showLocation ?? true}
                    onChange={(e) => handleSectionChange('privacy', 'showLocation', e.target.checked)}
                  />
                  <span>Show location on profile</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showLastActive || false}
                    onChange={(e) => handleSectionChange('privacy', 'showLastActive', e.target.checked)}
                  />
                  <span>Show last active status</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContentTab = () => {
    const section = formData.content || {} as UserPreferences['content'];
    return (
      <div className="preferences-section">
        <h3>📝 Content Preferences</h3>
        <p className="section-description">Configure how you create and manage content in SelfServe</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Blog Settings</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Default blog visibility</label>
                <select
                  value={section.defaultBlogVisibility || 'public'}
                  onChange={(e) => handleSectionChange('content', 'defaultBlogVisibility', e.target.value)}
                  className="form-select"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className="preference-item">
                <label>Default post status</label>
                <select
                  value={section.defaultPostStatus || 'draft'}
                  onChange={(e) => handleSectionChange('content', 'defaultPostStatus', e.target.value)}
                  className="form-select"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Writing Experience</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.autoSaveDrafts ?? true}
                    onChange={(e) => handleSectionChange('content', 'autoSaveDrafts', e.target.checked)}
                  />
                  <span>Auto-save drafts</span>
                </label>
              </div>
              {section.autoSaveDrafts && (
                <div className="preference-item">
                  <label>Auto-save interval (seconds)</label>
                  <select
                    value={section.autoSaveInterval || 30}
                    onChange={(e) => handleSectionChange('content', 'autoSaveInterval', parseInt(e.target.value))}
                    className="form-select"
                  >
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </select>
                </div>
              )}
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.showWordCount ?? true}
                    onChange={(e) => handleSectionChange('content', 'showWordCount', e.target.checked)}
                  />
                  <span>Show word count</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableMarkdownPreview ?? true}
                    onChange={(e) => handleSectionChange('content', 'enableMarkdownPreview', e.target.checked)}
                  />
                  <span>Enable markdown preview</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Comments</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.allowComments ?? true}
                    onChange={(e) => handleSectionChange('content', 'allowComments', e.target.checked)}
                  />
                  <span>Allow comments on posts by default</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.moderateComments || false}
                    onChange={(e) => handleSectionChange('content', 'moderateComments', e.target.checked)}
                  />
                  <span>Moderate comments before publishing</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDataTab = () => {
    const section = formData.data || {} as UserPreferences['data'];
    return (
      <div className="preferences-section">
        <h3>💾 Data Preferences</h3>
        <p className="section-description">Control your data format and regional settings</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Export Settings</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Export format</label>
                <select
                  value={section.exportFormat || 'json'}
                  onChange={(e) => handleSectionChange('data', 'exportFormat', e.target.value)}
                  className="form-select"
                >
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.includeAnalytics ?? true}
                    onChange={(e) => handleSectionChange('data', 'includeAnalytics', e.target.checked)}
                  />
                  <span>Include analytics in exports</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Regional Settings</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Timezone</label>
                <input
                  type="text"
                  value={section.timezone || 'America/New_York'}
                  onChange={(e) => handleSectionChange('data', 'timezone', e.target.value)}
                  className="form-input"
                  placeholder="e.g., America/New_York, Europe/London"
                />
              </div>
              <div className="preference-item">
                <label>Date format</label>
                <select
                  value={section.dateFormat || 'MM/DD/YYYY'}
                  onChange={(e) => handleSectionChange('data', 'dateFormat', e.target.value)}
                  className="form-select"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div className="preference-item">
                <label>Time format</label>
                <select
                  value={section.timeFormat || '12h'}
                  onChange={(e) => handleSectionChange('data', 'timeFormat', e.target.value)}
                  className="form-select"
                >
                  <option value="12h">12 hour</option>
                  <option value="24h">24 hour</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab();
      case 'privacy':
        return renderPrivacyTab();
      case 'content':
        return renderContentTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'data':
        return renderDataTab();
      default:
        return (
          <div className="preferences-section">
            <h3>Coming Soon</h3>
            <p>This preferences section is under development.</p>
          </div>
        );
    }
  };

  if (loading) {
    return <div className="preferences-page"><div className="loading">Loading preferences...</div></div>;
  }

  if (error && !preferences) {
    return <div className="preferences-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="preferences-page">
      <header className="page-header">
        <h1>⚙️ Preferences</h1>
        <div className="header-actions">
          {hasChanges && (
            <div className="edit-actions">
              <button className="btn btn-success" onClick={handleSave}>
                Save Changes
              </button>
              <button className="btn btn-secondary" onClick={handleDiscard}>
                Discard
              </button>
            </div>
          )}
          <button className="btn btn-outline" onClick={handleReset}>
            Reset to Defaults
          </button>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="preferences-content">
        <nav className="preferences-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="preferences-panel">
          {renderCurrentTab()}
        </div>
      </div>
    </div>
  );
};

export default Preferences;